<template>
  <div class="home-page px-4 py-4 md:px-5">
    <BasicLoading v-if="listPage.isLoading" />

    <div class="mx-auto space-y-5">
      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div class="space-y-3">
            <div>
              <p class="text-[15px] font-semibold text-slate-900">평생교육 홈페이지 관리자 안내</p>
            </div>
            <ul class="grid gap-x-10 gap-y-2 text-sm text-slate-600 md:grid-cols-2">
              <li v-for="item in guideItems" :key="item.step" class="guide-item">
                <span class="guide-dot">{{ item.step }}</span>
                <span>
                  <template v-for="(part, index) in item.parts" :key="index">
                    <span v-if="part.type === 'text'">{{ part.value }}</span>
                    <span v-else class="font-semibold text-red-500">{{ part.value }}</span>
                  </template>
                </span>
              </li>
            </ul>
          </div>

          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            @click="listPage.openModal()"
          >
            자동배정 안내 보기
          </button>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div class="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.45fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.9fr)]">
            <label class="filter-field">
              <span class="filter-label">HQ 매니저</span>
              <select class="filter-control" v-model="listPage.searchParams.staffIdx">
                <option value="0">전체</option>
                <option v-for="(item, i) in listPage.staffList" :key="i" :value="item.idx">
                  {{ item.ename }} ({{ item.kname }})
                </option>
              </select>
            </label>

            <label class="filter-field">
              <span class="filter-label">수강신청 기간</span>
              <div class="filter-control filter-datepicker">
                <BagicDatePickerRange
                  :eduStartDate="listPage.searchParams.eduStartDate"
                  :eduEndDate="listPage.searchParams.eduEndDate"
                  @date-click="listPage.setDate"
                />
              </div>
            </label>

            <label class="filter-field">
              <span class="filter-label">진행 상태</span>
              <select class="filter-control" v-model="listPage.searchParams.status">
                <option value="0">상태검색</option>
                <option value="processing">진행중</option>
                <option value="expected">예정</option>
                <option value="end">종료</option>
              </select>
            </label>

            <label class="filter-field">
              <span class="filter-label">검색 기준</span>
              <select class="filter-control" v-model="listPage.searchParams.searchValue">
                <option value="company">Company</option>
                <option value="class">과정명</option>
              </select>
            </label>

            <label class="filter-field">
              <span class="filter-label">도메인 설정</span>
              <select class="filter-control" v-model="listPage.searchParams.siteMetaStatus">
                <option value="all">전체</option>
                <option value="registered">수정</option>
                <option value="unregistered">등록</option>
              </select>
            </label>
          </div>

          <div class="grid gap-4 xl:min-w-[460px] xl:grid-cols-[minmax(0,1fr)_120px]">
            <label class="filter-field">
              <span class="filter-label">검색어</span>
              <input
                v-model="listPage.searchParams.searchKeyword"
                type="text"
                class="filter-control"
                placeholder="회사명 또는 과정명"
                @keyup.enter="listPage.search()"
              />
            </label>

            <button type="button" class="search-button" @click="listPage.search()">검색</button>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p class="text-base font-semibold text-slate-900">운영 중인 사이트 및 과정 목록</p>
            <p class="mt-1 text-sm text-slate-500">총 {{ listPage.totalCnt }}건</p>
          </div>
          <div class="flex flex-wrap gap-2 text-sm text-slate-500">
            <span class="result-chip">페이지 {{ listPage.searchParams.pageNum }}</span>
            <span class="result-chip">기준 {{ listPage.searchParams.searchValue === "company" ? "Company" : "과정명" }}</span>
          </div>
        </div>

        <div class="table-scroll-wrap rounded-xl border border-slate-200">
          <BagicTable
            :header="listPage.tableHeader"
            :list="listPage.tableList"
            :pageSize="listPage.searchParams.pageSize"
            :totalRows="listPage.totalCnt"
            :currentPage="listPage.searchParams.pageNum"
            @paging-click="listPage.movePage"
            :detailRowName="listPage.detailRowName"
            :detailParamName="listPage.detailParamName"
          />
        </div>
      </section>
    </div>

    <div v-if="listPage.modalOnOff" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="listPage.closeModal()"></div>

      <div
        class="relative max-h-[90vh] w-full max-w-[980px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 class="text-lg font-semibold text-slate-900">평생교육 글로벌역량 플랫폼 자동배정 안내</h3>
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-lg font-semibold text-slate-600 transition hover:bg-slate-200"
            @click="listPage.closeModal()"
          >
            ×
          </button>
        </div>

        <div class="max-h-[calc(90vh-74px)] overflow-y-auto px-6 py-5">
          <div class="grid gap-4 md:grid-cols-2">
            <article
              v-for="section in autoAssignSections"
              :key="section.title"
              class="rounded-xl border border-slate-200 bg-slate-50 p-5"
            >
              <p class="text-base font-semibold text-slate-900">{{ section.title }}</p>
              <div class="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                <p v-for="(paragraph, index) in section.paragraphs" :key="index">{{ paragraph }}</p>
              </div>
            </article>
          </div>

          <div class="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
            <p class="text-sm font-semibold text-slate-900">운영 참고</p>
            <div class="mt-2 space-y-1 text-sm leading-6 text-slate-600">
              <p v-for="(note, index) in autoAssignNotes" :key="index">{{ note }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Transition name="toast-fade">
      <div v-if="listPage.toastVisible" class="toast-notification">
        {{ listPage.toastMessage }}
      </div>
    </Transition>
  </div>
</template>
<script>
import { onMounted, reactive, nextTick, watch } from "vue";
import axios from "@/plugins/axios.js";

import BagicTable from "@/components/BagicTable.vue";
import BagicDatePickerRange from "@/components/BagicDatePickerRange.vue";
import BasicLoading from "@/components/BasicLoading.vue";

export default {
  name: "Home",
  components: {
    BagicTable,
    BagicDatePickerRange,
    BasicLoading,
  },
  setup() {
    const guideItems = [
      {
        step: "1",
        parts: [
          { type: "text", value: "" },
          { type: "highlight", value: "[Compnay] " },
          { type: "text", value: "클릭 시, 홈페이지 수정페이지로 이동합니다." },
        ],
      },
      {
        step: "2",
        parts: [
          { type: "text", value: "" },
          { type: "highlight", value: "[과정명] " },
          { type: "text", value: "클릭 시, 수강신청 수정페이지로 이동합니다." },
        ],
      },
      {
        step: "3",
        parts: [
          { type: "text", value: "학습자가 보게 될 화면은 미리보기 " },
          { type: "highlight", value: "[View] " },
          { type: "text", value: "버튼을 클릭하여 확인 가능합니다." },
        ],
      },
      {
        step: "4",
        parts: [
          { type: "text", value: "수강신청 리스트는 신청자/대기인원의 " },
          { type: "highlight", value: "[숫자]" },
          { type: "text", value: "를 클릭하여 확인 가능합니다." },
        ],
      },
      {
        step: "5",
        parts: [
          { type: "text", value: "수강신청 취소자 리스트는 취소자의 " },
          { type: "highlight", value: "[숫자]" },
          { type: "text", value: "를 클릭하여 확인 가능합니다." },
        ],
      },
      {
        step: "6",
        parts: [
          { type: "text", value: "공지사항 관리 " },
          { type: "highlight", value: "[View] " },
          { type: "text", value: "버튼을 클릭하여 공지사항 관리가 가능합니다." },
        ],
      },
      {
        step: "7",
        parts: [{ type: "text", value: "평생교육 글로벌역량 플랫폼 자동배정 안내" }],
      },
    ];

    const autoAssignSections = [
      {
        title: "[1:1 수업]",
        paragraphs: [
          "수강신청을 완료하고 결제가 이루어지는 즉시, 학습자가 기재한 희망 시간 1과 희망 시간 2 기준으로 자동배정이 진행됩니다.",
          "희망 시간 내 배정 가능한 강사가 없는 경우 해당 수강신청 자동배정은 진행되지 않습니다.",
          "자동배정까지 걸리는 시간은 약 1분 내외이며 강사와 교재까지 자동배정됩니다. 수강 신청이 집중되는 시간에는 일부 지연이 발생할 수 있습니다.",
        ],
      },
      {
        title: "[1:N 그룹 수업]",
        paragraphs: [
          "수강신청과 결제가 완료되면 학습자가 선택한 수업 기준으로 자동배정이 진행됩니다. 선택한 수업 내 배정 가능한 강사가 없으면 자동배정은 진행되지 않습니다.",
          "렉쳐 배정은 수업시작일 기준 +7일 사이에서 수업 횟수에 따른 요일 기준으로 가능한 일자 순으로 배정됩니다. 주 2회는 화/목, 주 3회는 월/수/금, 주 5회는 월/화/수/목/금 기준입니다.",
          "예를 들어 수업시작일이 3월 1일이고 월/수/금 주 3회 수업을 희망할 경우 3월 4일, 3월 6일, 3월 8일 순으로 조회하며 가능한 강사가 있는 시작일로 배정됩니다.",
          "자동배정은 약 1분 내외로 강사와 교재까지 자동배정되며, 배정 가능 강사가 없어 미배정된 신청 건은 매일 04:00 AM 재배정을 진행합니다. 재배정은 영업일 기준 수업 시작 3일 전까지의 신청 건만 해당됩니다.",
        ],
      },
    ];

    const autoAssignNotes = [
      "첫 수업 시작일은 강사 스케줄에 따라 교육기간 시작일로부터 최대 +7일까지만 배정됩니다.",
      "자동배정 진행사항은 관리자단의 신청자/대기인원 목록에서 배정유무로 확인할 수 있습니다.",
    ];

    const listPage = reactive({
      searchParams: {
        startDate: "",
        endDate: "",
        pageNum: 1,
        pageSize: 50,
        searchKeyword: "",
        searchValue: "company",
        siteMetaStatus: "all",
        staffIdx: 0,
        status: 0,
        idxLeSiteMain: "",
        eduStartDate: "",
        eduEndDate: "",
      },
      tableHeader: {
        rowNum: "No",
        companyName: "Company",
        sugangName: "과정명",
        classDate: "수강신청기간",
        hqmanger: "HQ 매니저",
        status: "상태",
        preview: "미리보기",
        applyWaitCount: "신청자/대기인원",
        currentSituation: "현황 Board",
        cancleCount: "취소자",
        notice: "공지사항",
        siteMetaConfig: "도메인 설정",
        siteMetaReset: "도메인 설정 초기화",
        copy: "복사",
      },
      rawTableList: [],
      tableList: [],
      staffList: [],
      detailRowName: "",
      detailParamName: "idxLeSiteMain",
      dateValue: "",
      totalCnt: 0,
      serverTotalCnt: 0,
      modalOnOff: false,
      isLoading: false,
      toastMessage: "",
      toastVisible: false,
      toastTimer: null,

      showToast(message) {
        listPage.toastMessage = message;
        listPage.toastVisible = true;

        if (listPage.toastTimer) {
          clearTimeout(listPage.toastTimer);
        }

        listPage.toastTimer = setTimeout(() => {
          listPage.toastVisible = false;
        }, 2000);
      },

      async copyUrlToClipboard(url) {
        try {
          await navigator.clipboard.writeText(url);
          listPage.showToast("URL이 복사되었습니다.");
        } catch (error) {
          console.error("URL 복사 중 오류가 발생했습니다:", error);
          listPage.showToast("URL 복사에 실패했습니다.");
        }
      },

      applySiteMetaFilter() {
        const baseList = Array.isArray(listPage.rawTableList) ? [...listPage.rawTableList] : [];
        const filterValue = listPage.searchParams.siteMetaStatus;

        if (filterValue === "registered") {
          listPage.tableList = baseList.filter((item) => item.hasSeoData);
          listPage.totalCnt = listPage.tableList.length;
          return;
        }

        if (filterValue === "unregistered") {
          listPage.tableList = baseList.filter((item) => !item.hasSeoData);
          listPage.totalCnt = listPage.tableList.length;
          return;
        }

        listPage.tableList = baseList;
        listPage.totalCnt = listPage.serverTotalCnt;
      },

      async loadSeoStatusForList() {
        if (!Array.isArray(listPage.tableList) || listPage.tableList.length === 0) {
          listPage.rawTableList = [];
          return;
        }

        const seoStatusList = await Promise.all(
          listPage.tableList.map(async (item) => {
            try {
              const res = await axios.get("/admin/siteMainSeo", {
                params: {
                  idxLeSiteMain: item.idxLeSiteMain,
                },
              });

              const hasSeoData = res.data.success && res.data.data && typeof res.data.data === "object";
              return {
                idxLeSiteMain: item.idxLeSiteMain,
                hasSeoData,
              };
            } catch (error) {
              console.error("SEO 설정 상태 조회 중 오류가 발생했습니다:", error);
              return {
                idxLeSiteMain: item.idxLeSiteMain,
                hasSeoData: false,
              };
            }
          })
        );

        const seoStatusMap = new Map(seoStatusList.map((item) => [item.idxLeSiteMain, item.hasSeoData]));

        listPage.tableList.forEach((item) => {
          const companyName = encodeURIComponent(item.originCompanyName || "");
          const hasSeoData = seoStatusMap.get(item.idxLeSiteMain);
          item.hasSeoData = hasSeoData;
          const buttonLabel = hasSeoData ? "수정" : "등록";
          const buttonClass = hasSeoData ? "table-action-button table-action-amber" : "table-action-button table-action-green";

          item.siteMetaConfig =
            "<a class='" +
            buttonClass +
            "' href='/siteMetaConfig?idxLeSiteMain=" +
            item.idxLeSiteMain +
            "&companyName=" +
            companyName +
            "' target='_blank' rel='noopener noreferrer'>" +
            buttonLabel +
            "</a>";
        });

        listPage.rawTableList = [...listPage.tableList];
        listPage.applySiteMetaFilter();
      },

      getClassList() {
        return axios
          .get("/admin/sugangList", {
            params: listPage.searchParams,
          })
          .then(function (res) {
            if (!res.data.success) {
              return;
            }

            listPage.totalCnt = res.data.data.totalCnt;
            listPage.serverTotalCnt = res.data.data.totalCnt;
            listPage.tableList = res.data.data.sugangList;
            listPage.rawTableList = [];

            if (Array.isArray(listPage.tableList)) {
              listPage.tableList.forEach(function (item) {
                item.originCompanyName = item.companyName || "";

                if (item.startDate === null || item.endDate === null) {
                  item.classDate = "미등록";
                } else {
                  item.classDate = item.startDate + " ~ " + item.endDate;
                }

                if (item.sugangName === null) {
                  item.sugangName = "미등록";
                }

                item.companyName = `<a class="font-semibold text-slate-900 underline underline-offset-4" href="/enrolmentStep1?idxLeSiteMain=${item.idxLeSiteMain}">${item.companyName}</a>`;

                const sugangNameText = item.sugangName.replace(/<br\s*\/?>/gi, " ");
                item.sugangName = !item.idxLeSugang
                  ? `<a class="table-course-link" href="/enrolmentStep2?idxLeSiteMain=${item.idxLeSiteMain}">${sugangNameText}</a>`
                  : `<a class="table-course-link" href="/enrolmentStep2?idxLeSugang=${item.idxLeSugang}">${sugangNameText}</a>`;

                const currentOrigin = window.location.origin;
                const isStage =
                  currentOrigin.includes("stage") || currentOrigin.includes("localhost") || currentOrigin.includes("local");

                const previewUrl = isStage
                  ? "https://stage-lifeedu.carrotenglish.net/home?idxLeSiteMain=" + item.idxLeSiteMain
                  : "https://lifeedu.carrotapply.com/home?idxLeSiteMain=" + item.idxLeSiteMain;

                item.sugangName +=
                  `<button role="button" data-copy-url="${previewUrl}" class="js-copy-url-btn table-copy-icon-btn" title="미리보기 URL 복사">` +
                  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="14" height="14"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" /></svg>` +
                  `</button>`;

                item.preview =
                  "<a class='table-action-button table-action-blue' href='" +
                  previewUrl +
                  "' target='_blank'>" +
                  "미리보기" +
                  "</a>";

                if (item.idxLeSugang) {
                  item.applyWaitCount = `<a class="font-bold text-[#0f766e] underline underline-offset-4" href="/applicantList?idxLeSugang=${item.idxLeSugang}">${item.applyWaitCount}</a>`;
                }

                if (item.isGroup == "Y") {
                  item.currentSituation =
                    "<a class='table-action-button table-action-slate' href='/currentSituationBoard?idxLeSugang=" +
                    item.idxLeSugang +
                    "'>" +
                    "현황 Board" +
                    "</a>";
                }

                item.cancleCount = `<a class="font-bold text-[#b91c1c] underline underline-offset-4" href="/cancelerList?idxLeSugang=${item.idxLeSugang}">${item.cancleCount}</a>`;

                item.notice =
                  "<a class='table-action-button table-action-slate' href='/noticeManagement?idxLeSiteMain=" +
                  item.idxLeSiteMain +
                  "'>View</a>";

                item.siteMetaConfig =
                  "<a class='table-action-button table-action-green' href='/siteMetaConfig?idxLeSiteMain=" +
                  item.idxLeSiteMain +
                  "&companyName=" +
                  encodeURIComponent(item.originCompanyName) +
                  "' target='_blank' rel='noopener noreferrer'>" +
                  "등록" +
                  "</a>";

                item.siteMetaReset = `<button role="button" data-idx-le-site-main="${item.idxLeSiteMain}" class="js-site-meta-reset-btn table-action-button table-action-red">초기화</button>`;

                item.copy = `<button role="button" data-idx-le-site-main="${item.idxLeSiteMain}" class="js-copy-btn table-action-button table-action-black">복사</button>`;
              });
            }

            listPage.searchParams.startDate = "";
            listPage.searchParams.endDate = "";

            nextTick(() => {
              document.querySelectorAll(".js-copy-btn").forEach((button) => {
                button.addEventListener("click", () => {
                  if (!confirm("복사하시겠습니까?")) {
                    return;
                  }
                  const idxLeSiteMain = button.getAttribute("data-idx-le-site-main");
                  listPage.copySugang(idxLeSiteMain);
                });
              });

              document.querySelectorAll(".js-site-meta-reset-btn").forEach((button) => {
                button.addEventListener("click", () => {
                  const idxLeSiteMain = button.getAttribute("data-idx-le-site-main");
                  listPage.resetSiteMeta(idxLeSiteMain);
                });
              });

              document.querySelectorAll(".js-copy-url-btn").forEach((button) => {
                button.addEventListener("click", (event) => {
                  event.stopPropagation();
                  const url = button.getAttribute("data-copy-url");
                  listPage.copyUrlToClipboard(url);
                });
              });
            });

            listPage.loadSeoStatusForList();
          });
      },

      async copySugang(idxLeSiteMain) {
        listPage.isLoading = true;
        try {
          const response = await axios.post("/admin/sugang/copy", null, {
            params: {
              idxLeSiteMain: Number(idxLeSiteMain),
            },
          });

          if (!response.data.success) {
            alert(response.data.message);
            return;
          }

          await listPage.getClassList();
          alert("복사가 완료되었습니다.");
        } catch (error) {
          console.error("복사 요청 중 오류가 발생했습니다:", error);
        } finally {
          listPage.isLoading = false;
        }
      },

      async resetSiteMeta(idxLeSiteMain) {
        if (!idxLeSiteMain) {
          alert("idxLeSiteMain 정보가 없습니다.");
          return;
        }

        if (!confirm("도메인 설정을 초기화하시겠습니까?")) {
          return;
        }

        listPage.isLoading = true;

        try {
          const response = await axios.delete("/admin/siteMainSeo", {
            params: {
              idxLeSiteMain,
            },
          });

          if (!response.data.success) {
            alert(response.data.message || "도메인 설정 초기화에 실패했습니다.");
            return;
          }

          await listPage.getClassList();
          alert("도메인 초기화 완료");
        } catch (error) {
          console.error("도메인 설정 초기화 요청 중 오류가 발생했습니다:", error);
          alert("도메인 설정 초기화 요청 중 오류가 발생했습니다.");
        } finally {
          listPage.isLoading = false;
        }
      },

      getStaffList() {
        axios.get("/admin/staffList", {}).then(function (res) {
          if (res.data.success) {
            listPage.staffList = res.data.data.companyList;
          }
        });
      },

      setDate(dateArr) {
        listPage.searchParams.startDate = dateArr[0];
        listPage.searchParams.endDate = dateArr[1];
      },

      search() {
        listPage.searchParams.pageNum = 1;
        listPage.getClassList();
      },

      movePage(value) {
        listPage.searchParams.pageNum = value;
        listPage.getClassList();
        listPage.getStaffList();
      },

      openModal() {
        listPage.modalOnOff = true;
      },

      closeModal() {
        listPage.modalOnOff = false;
      },
    });

    onMounted(() => {
      listPage.getClassList();
      listPage.getStaffList();
    });

    watch(
      () => listPage.dateValue,
      (cur) => {
        cur = cur.replace(/ /gi, "");
        const arr = cur.split("~");
        listPage.searchParams.startDate = arr[0];
        listPage.searchParams.endDate = arr[1];
      }
    );

    watch(
      () => listPage.searchParams.siteMetaStatus,
      () => {
        listPage.applySiteMetaFilter();
      }
    );

    return {
      autoAssignNotes,
      autoAssignSections,
      guideItems,
      listPage,
    };
  },
};
</script>
<style scoped>
.home-page {
  background: #f8fafc;
}

.guide-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  line-height: 1.7;
}

.guide-dot {
  display: inline-flex;
  height: 1.5rem;
  width: 1.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #334155;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.filter-control {
  height: 2.875rem;
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 0.75rem;
  background-color: #fff;
  padding: 0.7rem 0.9rem;
  font-size: 0.95rem;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.filter-control:focus {
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.15);
}

.filter-datepicker {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 0.9rem;
}

.search-button {
  margin-top: auto;
  display: inline-flex;
  height: 2.875rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #2563eb;
  border-radius: 0.75rem;
  background: #fff;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2563eb;
  transition: background-color 0.2s ease;
}

.search-button:hover {
  background: #f8fafc;
}

.result-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  background: #f8fafc;
  padding: 0.4rem 0.8rem;
}

.table-scroll-wrap {
  max-height: calc(100vh - 260px);
  overflow: auto;
}

:deep(table) {
  margin-bottom: 0;
  table-layout: auto;
}

:deep(thead tr) {
  background: #f8fafc;
}

:deep(th) {
  border-color: #e2e8f0;
  padding: 0.85rem 0.65rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
}

:deep(td) {
  border-color: #e2e8f0;
  padding: 0.85rem 0.65rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
  background-color: #fff;
  white-space: nowrap;
}

:deep(tbody tr:nth-child(even) td) {
  background-color: #fbfdff;
}

:deep(tbody tr:hover td) {
  background-color: #f8fbff;
}

:deep(td p) {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 2rem;
}

:deep(td a),
:deep(td button) {
  white-space: nowrap;
}

:deep(.table-action-button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.5rem;
  min-height: 2.375rem;
  padding: 0.45rem 0.9rem;
  border: 1px solid currentColor;
  border-radius: 0.85rem;
  background: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

:deep(.table-action-button:hover) {
  background: #f8fafc;
}

:deep(.table-action-blue) {
  color: #1d4ed8;
}

:deep(.table-action-green) {
  color: #059669;
}

:deep(.table-action-red) {
  color: #dc2626;
}

:deep(.table-action-slate) {
  color: #334155;
}

:deep(.table-action-black) {
  color: #0f172a;
}

:deep(.table-action-amber) {
  color: #d97706;
}

:deep(.table-course-link) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.95rem;
  border-radius: 0.75rem;
  border: 1px solid #0369a1;
  background: #fff;
  color: #0369a1;
  font-weight: 700;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

:deep(.table-course-link:hover) {
  background: #f8fafc;
}

:deep(.vue-tailwind-datepicker input) {
  border: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  background: transparent;
  font-size: 0.95rem;
  color: #0f172a;
  box-shadow: none;
}

:deep(.vue-tailwind-datepicker) {
  width: 100%;
}

:deep(.vue-tailwind-datepicker .relative) {
  width: 100%;
}

:deep(.vue-tailwind-datepicker input:focus) {
  box-shadow: none;
}

:global([id^="headlessui-popover-overlay-"]) {
  z-index: 1;
}

:deep(.bg-white.border-t.border-gray-200) {
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

:deep(nav[aria-label="Pagination"]) {
  gap: 0.25rem;
}

:deep(nav[aria-label="Pagination"] > span),
:deep(nav[aria-label="Pagination"] .relative.inline-flex) {
  border-radius: 14px;
}

:deep(nav[aria-label="Pagination"] .z-10) {
  background: #1d4ed8;
  color: #fff;
}

@media (max-width: 1024px) {
  :deep(table) {
    min-width: 1200px;
  }
}

.toast-notification {
  position: fixed;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  padding: 0.75rem 1.25rem;
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.92);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.25);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 0.5rem);
}

:deep(.table-copy-icon-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.4rem;
  width: 1.6rem;
  height: 1.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #fff;
  color: #475569;
  vertical-align: middle;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

:deep(.table-copy-icon-btn:hover) {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
}
</style>
